import { collection, getDocs, getDocsFromCache, query, orderBy, getFirestore, onSnapshot, deleteDoc, limit, writeBatch, doc, Unsubscribe } from 'firebase/firestore';
import { getFirebaseApp } from '../firebaseApp';
import type { Message } from '../../types/Inbox';

const SMS_CACHE_LIMIT = 30;
/** How many of a conversation's newest messages are read from the cache. Fewer than this back means the whole conversation was returned. */
export const MESSAGE_CACHE_LIMIT = 30;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FirestoreMsg = any;

/**
 * Firestore Cache Service for Messages
 * Implements 30-message LRU cache per conversation
 * Wix is source of truth; Firestore is cache layer only
 */
export class FirestoreCacheService {
  private static instance: FirestoreCacheService | null = null;
  private unsubscribers: Map<string, Unsubscribe> = new Map();
  private static readonly CACHE_LIMIT = MESSAGE_CACHE_LIMIT;

  static getInstance(): FirestoreCacheService {
    if (!FirestoreCacheService.instance) {
      FirestoreCacheService.instance = new FirestoreCacheService();
    }
    return FirestoreCacheService.instance;
  }

  private getFirestore() {
    return getFirestore(getFirebaseApp(), 'wix-chat');
  }

  private getTwilioFirestore() {
    return getFirestore(getFirebaseApp(), 'twilio-communications');
  }

  private isSmsId(id: string): boolean {
    return id.startsWith('sms-');
  }

  private smsPhone(id: string): string {
    return `+${id.replace('sms-', '')}`;
  }

  private async getSmsMessages(conversationId: string): Promise<Message[]> {
    const db = this.getTwilioFirestore();
    const phone = this.smsPhone(conversationId);
    try {
      const messagesRef = collection(db, 'sms-conversations', phone, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(SMS_CACHE_LIMIT));
      let snapshot = await getDocsFromCache(q).catch(() => null);
      if (!snapshot || snapshot.empty) snapshot = await getDocs(q);
      return snapshot.docs.map(d => {
        const data = d.data();
        const ts: Date = data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp || 0);
        return {
          id: d.id,
          _id: d.id,
          text: data.body || '',
          channel: 'sms',
          _createdDate: ts,
          timestamp: ts,
          isFromCustomer: data.direction === 'inbound',
          direction: data.direction,
          from: data.direction === 'outbound' ? (data.senderStaffName || data.from || '') : (data.from || ''),
          to: data.to || '',
          senderStaffName: data.senderStaffName || undefined,
          senderStaffAvatar: data.senderStaffAvatar || undefined,
          senderStaffId: data.senderStaffId || undefined,
        } as unknown as Message;
      });
    } catch (error) {
      console.error(`❌ Error loading SMS messages for ${conversationId}:`, error);
      return [];
    }
  }

  private subscribeSmsMessages(conversationId: string, callback: (messages: Message[]) => void): Unsubscribe {
    const db = this.getTwilioFirestore();
    const phone = this.smsPhone(conversationId);
    const messagesRef = collection(db, 'sms-conversations', phone, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(SMS_CACHE_LIMIT));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages: Message[] = snapshot.docs.map(d => {
        const data = d.data();
        const ts: Date = data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp || 0);
        return {
          id: d.id,
          _id: d.id,
          text: data.body || '',
          channel: 'sms',
          _createdDate: ts,
          timestamp: ts,
          isFromCustomer: data.direction === 'inbound',
          direction: data.direction,
          from: data.direction === 'outbound' ? (data.senderStaffName || data.from || '') : (data.from || ''),
          to: data.to || '',
          senderStaffName: data.senderStaffName || undefined,
          senderStaffAvatar: data.senderStaffAvatar || undefined,
          senderStaffId: data.senderStaffId || undefined,
        } as unknown as Message;
      });
      callback(messages);
    }, (error) => {
      console.error(`❌ [SMS subscription] Error for ${conversationId}:`, error);
    });
    this.unsubscribers.set(`messages_${conversationId}`, unsubscribe);
    return unsubscribe;
  }

  async getCachedMessages(conversationId: string | number): Promise<Message[]> {
    const conversationIdStr = String(conversationId);
    if (this.isSmsId(conversationIdStr)) {
      return this.getSmsMessages(conversationIdStr);
    }
    const db = this.getFirestore();
    try {
      console.log(`📚 Loading cached messages for conversation: ${conversationIdStr}`);
      const messagesRef = collection(db, `conversations/${conversationIdStr}/messages`);
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(FirestoreCacheService.CACHE_LIMIT));
      // Local cache first: instant when this conversation has been opened before. The live listener started
      // right after fills in anything newer, so showing the cached page first is safe. Empty cache -> server.
      let snapshot = await getDocsFromCache(q).catch(() => null);
      if (!snapshot || snapshot.empty) snapshot = await getDocs(q);
      if (snapshot.empty) {
        // Diagnostic: the ordered query found nothing. Is the collection really empty, or do its documents lack the
        // 'timestamp' field the ordering needs (orderBy skips such documents)?
        const probe = await getDocs(query(messagesRef, limit(3))).catch(() => null);
        console.warn(`[FirestoreCache] conversations/${conversationIdStr}/messages: ordered query returned 0; unordered probe found ${probe ? probe.size : 'error'} doc(s)`, probe?.docs.map(d => ({ id: d.id, fields: Object.keys(d.data()), timestamp: d.data().timestamp ?? null })));
      }
      const messages: FirestoreMsg[] = snapshot.docs.map(d => {
        const data: FirestoreMsg = d.data();
        if (data.timestamp?.toDate) data.timestamp = data.timestamp.toDate();
        if (!data.timestamp && typeof data._createdDate === 'string') data.timestamp = new Date(data._createdDate);
        if (!data.timestamp && typeof data.createdDate === 'string') data.timestamp = new Date(data.createdDate);
        data.id = data.wixMessageId || data._id || d.id;
        return data;
      });
      const seenIds = new Set<unknown>();
      const deduped: FirestoreMsg[] = [];
      messages.sort((a, b) => ((b.timestamp instanceof Date ? b.timestamp.getTime() : 0) - (a.timestamp instanceof Date ? a.timestamp.getTime() : 0)));
      for (const msg of messages) {
        if (!seenIds.has(msg.id)) { seenIds.add(msg.id); deduped.push(msg); }
        else console.warn(`⚠️ Skipping duplicate message ID: ${msg.id}`);
      }
      console.log(`✅ Retrieved ${deduped.length} cached messages (${messages.length - deduped.length} duplicates removed)`);
      return deduped as Message[];
    } catch (error) {
      console.error(`❌ Error retrieving cached messages for ${conversationIdStr}:`, error);
      return [];
    }
  }

  async getCachedMessagesPaginated(conversationId: string | number, pageSize = 50, pageNumber = 0): Promise<Message[]> {
    const db = this.getFirestore();
    const conversationIdStr = String(conversationId);
    try {
      const messagesRef = collection(db, `conversations/${conversationIdStr}/messages`);
      const snapshot = await getDocs(query(messagesRef, orderBy('timestamp', 'desc')));
      const allMessages: FirestoreMsg[] = snapshot.docs.map(d => {
        const data: FirestoreMsg = d.data();
        if (data.timestamp?.toDate) data.timestamp = data.timestamp.toDate();
        if (typeof data._createdDate === 'string') data._createdDate = new Date(data._createdDate);
        data.id = data.wixMessageId || data._id || d.id;
        return data;
      });
      const seenIds = new Set<unknown>();
      const deduped = allMessages.filter(msg => {
        if (seenIds.has(msg.id)) { console.warn(`⚠️ Skipping duplicate: ${msg.id}`); return false; }
        seenIds.add(msg.id); return true;
      });
      return deduped.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize) as Message[];
    } catch (error) {
      console.error(`❌ Error retrieving paginated messages:`, error);
      return [];
    }
  }

  subscribeToMessages(conversationId: string | number, callback: (messages: Message[]) => void): Unsubscribe {
    const conversationIdStr = String(conversationId);
    if (this.isSmsId(conversationIdStr)) {
      return this.subscribeSmsMessages(conversationIdStr, callback);
    }
    const db = this.getFirestore();
    console.log(`🔔 [FIRESTORE LISTENER] subscribeToMessages called for conversation: ${conversationIdStr}`);
    try {
      const messagesRef = collection(db, `conversations/${conversationIdStr}/messages`);
      // Only the newest messages — the same window the initial load shows. Without a limit the
      // server's answer completed the listener to the WHOLE conversation seconds after the page
      // opened (and downloaded its entire history on every open), swapping the on-screen list for it.
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(FirestoreCacheService.CACHE_LIMIT));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log(`💬 [FIRESTORE LISTENER] onSnapshot fired! Snapshot size: ${snapshot.docs.length}`);
        const messages: FirestoreMsg[] = snapshot.docs.map(d => {
          const data: FirestoreMsg = d.data();
          if (data.timestamp?.toDate) data.timestamp = data.timestamp.toDate();
          if (!data.timestamp && typeof data._createdDate === 'string') data.timestamp = new Date(data._createdDate);
          if (!data.timestamp && typeof data.createdDate === 'string') data.timestamp = new Date(data.createdDate);
          if (!data.id && !data._id) data.id = d.id;
          return data;
        });
        if (messages.length > 1) {
          const first = messages[0].timestamp || messages[0]._createdDate;
          const last = messages[messages.length - 1].timestamp || messages[messages.length - 1]._createdDate;
          const firstTime = first instanceof Date ? first.getTime() : new Date(String(first)).getTime();
          const lastTime = last instanceof Date ? last.getTime() : new Date(String(last)).getTime();
          if (firstTime < lastTime) messages.reverse();
        }
        console.log(`💬 Real-time update: ${messages.length} messages in conversation ${conversationIdStr}`);
        callback(messages as Message[]);
      }, (error) => {
        console.error(`❌ [FIRESTORE LISTENER] Error for ${conversationIdStr}:`, error);
      });
      this.unsubscribers.set(`messages_${conversationIdStr}`, unsubscribe);
      return unsubscribe;
    } catch (error) {
      console.error(`❌ [FIRESTORE LISTENER] CATCH ERROR for ${conversationIdStr}:`, error);
      return () => {};
    }
  }

  unsubscribeFromMessages(conversationId: string | number): void {
    const key = `messages_${conversationId}`;
    const unsubscribe = this.unsubscribers.get(key);
    if (unsubscribe) { unsubscribe(); this.unsubscribers.delete(key); }
  }

  unsubscribeAll(): void {
    this.unsubscribers.forEach(unsub => { try { unsub(); } catch (err) { console.error('Error unsubscribing:', err); } });
    this.unsubscribers.clear();
  }

  async getMessageCount(conversationId: string | number): Promise<number> {
    const db = this.getFirestore();
    try {
      const messagesRef = collection(db, `conversations/${String(conversationId)}/messages`);
      return (await getDocs(query(messagesRef))).size;
    } catch (error) {
      console.error(`❌ Error getting message count:`, error);
      return 0;
    }
  }

  async enforceCacheLimit(conversationId: string | number): Promise<void> {
    const db = this.getFirestore();
    const conversationIdStr = String(conversationId);
    try {
      const messagesRef = collection(db, `conversations/${conversationIdStr}/messages`);
      const messageCount = (await getDocs(query(messagesRef))).size;
      if (messageCount > FirestoreCacheService.CACHE_LIMIT) {
        const oldestSnapshot = await getDocs(query(messagesRef, orderBy('_createdDate', 'asc'), limit(1)));
        if (!oldestSnapshot.empty) await deleteDoc(oldestSnapshot.docs[0].ref);
      }
    } catch (error) {
      console.error(`❌ Error enforcing cache limit for ${conversationId}:`, error);
    }
  }

  async initializeConversationCache(conversationId: string | number, wixMessages: Message[]): Promise<void> {
    const db = this.getFirestore();
    const conversationIdStr = String(conversationId);
    try {
      console.log(`🔄 Initializing cache for ${conversationIdStr} with ${wixMessages.length} messages...`);
      await this.bulkCleanupConversation(conversationId);
      const messagesToCache = wixMessages.slice(-FirestoreCacheService.CACHE_LIMIT);
      const messagesRef = collection(db, `conversations/${conversationIdStr}/messages`);
      const batch = writeBatch(db);
      messagesToCache.forEach((msg) => {
        const msgRecord = msg as FirestoreMsg;
        const wixMessageId = String(msgRecord._id || msgRecord.id || '');
        batch.set(doc(messagesRef), { ...msgRecord, wixMessageId, _cachedAt: new Date() });
      });
      await batch.commit();
      console.log(`✅ Cache initialized for ${conversationIdStr}: ${messagesToCache.length} messages cached`);
    } catch (error) {
      console.error(`❌ Error initializing cache for ${conversationId}:`, error);
      throw error;
    }
  }

  async bulkCleanupConversation(conversationId: string | number): Promise<void> {
    const db = this.getFirestore();
    const conversationIdStr = String(conversationId);
    try {
      const messagesRef = collection(db, `conversations/${conversationIdStr}/messages`);
      const allSnapshot = await getDocs(query(messagesRef, orderBy('_createdDate', 'desc')));
      if (allSnapshot.size <= FirestoreCacheService.CACHE_LIMIT) return;
      const toDelete = allSnapshot.docs.slice(FirestoreCacheService.CACHE_LIMIT);
      for (let i = 0; i < toDelete.length; i += 500) {
        const batch = writeBatch(db);
        toDelete.slice(i, i + 500).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    } catch (error) {
      console.error(`❌ Error in bulk cleanup for ${conversationId}:`, error);
      throw error;
    }
  }

  async searchMessages(conversationId: string | number, searchText: string): Promise<Message[]> {
    const messages = await this.getCachedMessages(conversationId);
    const lower = searchText.toLowerCase();
    return messages.filter(msg => {
      const m = msg as FirestoreMsg;
      return m.text?.toLowerCase().includes(lower) || (m.senderName || '').toLowerCase().includes(lower);
    });
  }
}
